/**
 * Alt-drag: hold Alt, press on a note, rail node or wall, and drag it across the grid
 * (docs/spec/srvr/gizmos.md SRVR-0024). The point grabbed stays under the cursor: the pointer is
 * projected onto a plane parallel to the grid at the depth of that point, and the object moves by
 * the pointer's displacement on that plane. A fixed pixels-to-units gain (what this replaced) drifts
 * away from the cursor by an amount that depends on the object's distance and the camera.
 *
 * Notes and rail nodes are snapped when Snap to Grid is on, then clamped to the placement limits —
 * here (the scene half of the dual clamp, style rule 9) and again in the store. In Synth VR a wall is snapped
 * too; walls are never clamped (SRVR-0065).
 *
 * Lives outside TunnelScene so ObstacleModel's wall pools can start a drag without a circular import,
 * and so the arithmetic can be unit-tested without a Canvas. The drag runs on window listeners, not
 * mesh events, so it keeps going when the pointer leaves the object — and when a selected note's
 * mesh is swapped between the instanced pool and its classic mesh mid-drag.
 */
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import { useEditorStore } from '../../store/editorStore';
import type { ObstacleMove } from '../../store/editorStore';
import { NOTE_TO_WORLD, clampToPlacementLimits } from './placementGeometry';
import { wallFileXY, WALL_ROTATION_STEP, CROUCH_MATCH_MS } from './wallGeometry';
import { isSynthVrView } from '../../store/editorSettings';
import { effectiveAreaRange, inRange, noteFilterKind } from '../../store/areaSelection';
import { gestureMatches, keyGestureMatches } from './gestures';

/** What an Alt-press landed on. Slides, squares and triangles by index in their own list; a crouch by
 *  its time, which is all the format gives it. */
export type DragTarget =
  | { kind: 'note'; index: number }
  | { kind: 'railNode'; noteIndex: number; nodeIndex: number }
  | { kind: 'slide' | 'square' | 'triangle'; index: number }
  | { kind: 'crouch'; timeMs: number };

/** One object the drag moves, with its X/Y (note space) and third component when the drag began. */
export interface DragMember {
  target: DragTarget;
  start: [number, number];
  /** Notes and rail nodes: `position[2]`, written back unchanged. */
  z: number;
}

export interface MovePlan {
  /** `offGrid`: written without the placement clamp (a group move, or a Snap to Rail landing). */
  notes: Array<{ index: number; position: [number, number, number]; offGrid?: boolean }>;
  railNode: { noteIndex: number; nodeIndex: number; position: [number, number, number]; offGrid?: boolean } | null;
  /** Every rail node the plan writes: `railNode` is the last of these. */
  railNodes: Array<{ noteIndex: number; nodeIndex: number; position: [number, number, number]; offGrid?: boolean }>;
  obstacles: ObstacleMove[];
}

type XY = [number, number];

const isGridObject = (t: DragTarget) => t.kind === 'note' || t.kind === 'railNode';

// ── Pure arithmetic ──────────────────────────────────────────────────────────

const _ndc = new THREE.Vector2();
const _raycaster = new THREE.Raycaster();
const _plane = new THREE.Plane();
const _normal = new THREE.Vector3(0, 0, 1);

/** Where the camera ray through a client pixel meets the plane `z = planeZ` (world units), or null
 *  when the ray runs parallel to it or points away. */
export function pointerOnPlane(
  clientX: number, clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  camera: THREE.Camera, planeZ: number,
): THREE.Vector3 | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  _ndc.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
  _raycaster.setFromCamera(_ndc, camera);
  _plane.set(_normal, -planeZ);
  return _raycaster.ray.intersectPlane(_plane, new THREE.Vector3());
}

/** A world-space displacement in note space. */
export function noteDelta(from: { x: number; y: number }, to: { x: number; y: number }): XY {
  return [(to.x - from.x) / NOTE_TO_WORLD, (to.y - from.y) / NOTE_TO_WORLD];
}

/**
 * The positions a drag writes for the pointer's note-space displacement `delta`. The grabbed object
 * decides the offset: a note or rail node is snapped (when `snap` is given), and a grabbed wall moves
 * by `delta` exactly. The group then moves as one rigid body: every member moves by the same offset, so the
 * members never change position relative to one another. The grabbed note or rail node is always held
 * inside the placement limits — one that starts outside them snaps back onto the grid — and the members
 * that follow it are not held there: in a group of two or more notes and rail nodes they are planned
 * `offGrid` (SRVR-0369). A single note or rail node is cut back, on each axis, just far enough to stay
 * inside the limits, and the walls that move with it follow whatever offset that allows. Walls are never
 * clamped (SRVR-0065).
 */
export function planMove(
  grabbed: DragMember, group: DragMember[], delta: XY,
  snap: ((x: number, y: number) => XY) | null,
  clamp: (x: number, y: number) => XY,
  rigid = true,
  snapWall: ((x: number, y: number) => XY) | null = snap,
): MovePlan {
  let off: XY = delta;
  if (isGridObject(grabbed.target)) {
    let p: XY = [grabbed.start[0] + delta[0], grabbed.start[1] + delta[1]];
    if (snap) p = snap(p[0], p[1]);
    // The grabbed note or rail node is clamped on its own and the rest follow. In Synth VR this is what
    // holds the object in the mapper's hand on the grid, even when it started off it (it snaps back on the
    // first step); only the followers of a group may end up off the grid. Outside Synth VR it is the way
    // every view moved a group before.
    p = clamp(p[0], p[1]);
    off = [p[0] - grabbed.start[0], p[1] - grabbed.start[1]];
  } else if (rigid && snapWall) {
    // Synth VR, Snap to Grid on: a grabbed wall's position lands on the nearest point of the grid's lines, which
    // continue past the grid for a wall, and the rest of the group moves by that offset, as WallDragger.cs does
    // (GetNearestPointOnGrid on the wall's position). A wall is snapped but never clamped (SRVR-0065).
    const p = snapWall(grabbed.start[0] + delta[0], grabbed.start[1] + delta[1]);
    off = [p[0] - grabbed.start[0], p[1] - grabbed.start[1]];
  }
  const gridMembers = rigid ? group.filter(m => isGridObject(m.target)) : [];
  // A group of notes and rail nodes moves freely in Synth VR and may leave the grid; one note is still held
  // inside it, as the mapper positions it by hand.
  const free = gridMembers.length > 1;
  // A member is clamped on its own only when it moves alone (or outside Synth VR). In a group the offset is
  // what is cut back, so a note that already sits off the grid is not pulled to the edge while a wall beside
  // it moves by the plain offset: that would break the rigid body (SRVR-0018 AC11).
  const clampMember = !free && !(rigid && group.length > 1);
  if (gridMembers.length && !free) {
    // The limits, read through the caller's clamp so this and the store's agree on what they are.
    const lo = clamp(-1e9, -1e9), hi = clamp(1e9, 1e9);
    const minX = Math.min(...gridMembers.map(m => m.start[0])), maxX = Math.max(...gridMembers.map(m => m.start[0]));
    const minY = Math.min(...gridMembers.map(m => m.start[1])), maxY = Math.max(...gridMembers.map(m => m.start[1]));
    // A group that already starts outside a limit (an imported map can) is held where it is on that
    // side, never pulled back to it: the room to move is at least zero each way.
    off = [
      Math.max(Math.min(0, lo[0] - minX), Math.min(Math.max(0, hi[0] - maxX), off[0])),
      Math.max(Math.min(0, lo[1] - minY), Math.min(Math.max(0, hi[1] - maxY), off[1])),
    ];
  }
  const plan: MovePlan = { notes: [], railNode: null, railNodes: [], obstacles: [] };
  for (const m of group) {
    const x = m.start[0] + off[0], y = m.start[1] + off[1];
    const t = m.target;
    if (t.kind === 'note') {
      const [cx, cy] = clampMember ? clamp(x, y) : [x, y];
      plan.notes.push({ index: t.index, position: [cx, cy, m.z], ...(clampMember ? {} : { offGrid: true }) });
    } else if (t.kind === 'railNode') {
      const [cx, cy] = clampMember ? clamp(x, y) : [x, y];
      plan.railNode = { noteIndex: t.noteIndex, nodeIndex: t.nodeIndex, position: [cx, cy, m.z], ...(clampMember ? {} : { offGrid: true }) };
      plan.railNodes.push(plan.railNode);
    } else if (t.kind === 'crouch') {
      plan.obstacles.push({ kind: 'crouch', timeMs: t.timeMs, x, y });
    } else {
      plan.obstacles.push({ kind: t.kind, index: t.index, x, y });
    }
  }
  return plan;
}

/**
 * One pointer step of a move-handle drag on a member of a multi-selection: the pointer's pixel movement
 * (`dx`, `dy`) times `gain` is the offset on the handle's axis, snapped on that axis only as the
 * single-object paths do, and `planMove` carries it to every member. Written to the store here.
 */
export function applyGroupHandleDrag(
  store: StoreState, group: { grabbed: DragMember; members: DragMember[] }, axis: 'x' | 'y',
  dx: number, dy: number, gain: number, clamp: (x: number, y: number) => XY,
): void {
  const onX = axis === 'x';
  const delta: XY = onX ? [dx * gain, 0] : [0, -dy * gain];
  const axisSnap = (to: (x: number, y: number) => XY) => (x: number, y: number): XY => {
    const [sx, sy] = to(x, y);
    return onX ? [sx, y] : [x, sy];
  };
  const snap = store.snapToGrid ? axisSnap(store.snapToGridPosition) : null;
  const snapWall = store.snapToGrid ? axisSnap(store.snapWallToGridPosition) : null;
  const plan = planMove(group.grabbed, group.members, delta, snap, clamp, isSynthVrView(store.activeView), snapWall);
  if (plan.notes.length) store.moveSelectedNotes(plan.notes);
  for (const r of plan.railNodes) store.updateRailNode(r.noteIndex, r.nodeIndex, r.position, r.offGrid);
  if (plan.obstacles.length) store.moveObstacles(plan.obstacles);
}

/** The undo label: what SRVR-0015 already calls these moves, plus `Move N objects` for a group that
 *  holds walls. */
export function dragLabel(group: DragMember[]): string {
  if (group.length === 1) {
    const k = group[0].target.kind;
    return k === 'note' ? 'Move note' : k === 'railNode' ? 'Move rail node' : 'Move obstacle';
  }
  return group.every(m => m.target.kind === 'note') ? `Move ${group.length} notes` : `Move ${group.length} objects`;
}

// ── Picking pooled objects ──────────────────────────────────────────────────

/**
 * Call after rewriting a pickable pool's instances. Three.js tests a ray against an InstancedMesh's
 * bounding sphere first and computes that sphere once, on the first raycast, from the instances that
 * were there then. A pool rewrites its instances every frame, so the cached sphere soon describes
 * objects that have moved: a note seeded at one depth and drawn at another was visible and could not
 * be pressed. Dropping it makes the next raycast recompute it — lazily, so it costs nothing until the
 * pointer moves. See docs/knowledge/instanced-pick-bounds.md.
 */
/** A raycast that finds nothing: set on an object that is drawn but must not be picked — a note or wall
 *  behind the playhead, which the mapper sees faded and can no longer grab. */
export const NO_RAYCAST = (): void => undefined;

/**
 * The raycast of an instanced pool that holds the objects behind the playhead (the dim and faded pools):
 * in Synth VR a ray passes through them, in every other view they are picked as they always were. It
 * decides on each ray, since the pool is mounted in both views and does not re-render on a view change.
 */
export function dimPoolRaycast(this: THREE.InstancedMesh, raycaster: THREE.Raycaster, hits: THREE.Intersection[]): void {
  if (isSynthVrView(useEditorStore.getState().activeView)) return;
  THREE.InstancedMesh.prototype.raycast.call(this, raycaster, hits);
}

export function dropPickBounds(mesh: THREE.InstancedMesh): void {
  mesh.boundingSphere = null;
}

// ── Alt + right-click: take the playhead to an object's beat ────────────────

/** Both right-button gestures belong to Synth VR: Console keeps its right button as it was. They work in
 *  every camera, free-look ones included — the free camera ignores a right press with Alt or Ctrl held. */
const notVr = () => !isSynthVrView(useEditorStore.getState().activeView);

/** An Alt + right press, the mappers' gesture for "go to this object". Read from the native event so
 *  it serves pointer and context-menu events alike. */
export function isAltRightPress(e: { nativeEvent: { button?: number; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean } }): boolean {
  return !notVr() && gestureMatches('snap_grid_to_object', e.nativeEvent);   // Alt + Mouse1 (the right button) unless rebound
}

/** The Remote Delete gesture, Ctrl (or Cmd) + Mouse1 (the right button) unless rebound. A press that is not
 *  that one never deletes. */
export function isCtrlRightPress(e: { nativeEvent: { button?: number; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean } }): boolean {
  return !notVr() && gestureMatches('remote_delete', e.nativeEvent);
}

/**
 * Move the playhead to `objectBeat` when the object is on the grid or still ahead of it, and leave it
 * alone when the object is already behind. The beat is exact — an object off the Step grid is still
 * landed on, which is why this does not go through the snapping `setCurrentTime`. Does nothing while
 * the track is playing. Returns whether it moved.
 */
export function jumpPlayheadToObject(objectBeat: number): boolean {
  const st = useEditorStore.getState();
  if (st.playback.playing) return false;
  const bpm = st.beatmap?.bpm || 120;
  const offsetSec = (st.beatmap?.offset || 0) / 1000;
  const nowBeat = ((st.playback.currentTime - offsetSec) / 60) * bpm;
  const onGrid = Math.max(0.006, st.scrollStep * 0.55);   // the tolerance every on-grid test uses
  if (objectBeat < nowBeat - onGrid) return false;
  st.setCurrentBeat(objectBeat);
  return true;
}

// ── Reading the store ───────────────────────────────────────────────────────

type StoreState = ReturnType<typeof useEditorStore.getState>;

/** The target with its start position, or null when it no longer exists. Walls start where they are
 *  drawn from: their file position, or the format centre when they have none (wallFileXY). */
export function memberOf(state: StoreState, target: DragTarget): DragMember | null {
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  if (!diff) return null;
  switch (target.kind) {
    case 'note': {
      const n = diff.notes[target.index];
      return n ? { target, start: [n.position[0], n.position[1]], z: n.position[2] } : null;
    }
    case 'railNode': {
      const node = diff.notes[target.noteIndex]?.rail_nodes?.[target.nodeIndex];
      return node ? { target, start: [node[0], node[1]], z: node[2] } : null;
    }
    case 'slide': {
      const s = diff.slides[target.index];
      return s ? { target, start: wallFileXY(s.position), z: 0 } : null;
    }
    case 'square':
    case 'triangle': {
      const o = (target.kind === 'square' ? diff.squares : diff.triangles)?.[target.index];
      return o ? { target, start: wallFileXY(o.position), z: 0 } : null;
    }
    case 'crouch': {
      if (!diff.crouches.some(t => Math.abs(t - target.timeMs) <= CROUCH_MATCH_MS)) return null;
      const o = diff.crouch_obstacles?.find(c => Math.abs(c.time_ms - target.timeMs) <= CROUCH_MATCH_MS);
      return { target, start: wallFileXY(o?.position), z: 0 };
    }
  }
}

/** Everything the drag moves: the whole selection — notes and every kind of wall — when the grabbed
 *  object is part of it, otherwise just the grabbed object. An area selection is such a selection, so
 *  grabbing one object in the area moves all that the Selection Options let through. A rail node grabbed
 *  inside a selected area (Synth VR) is the exception: it moves every rail node inside the area, on any
 *  rail the options let through, and nothing else. */
export function dragGroup(state: StoreState, grabbed: DragMember): DragMember[] {
  const t = grabbed.target;
  // Only Synth VR selects squares, triangles and crouches; another view moves what it always did.
  const vr = isSynthVrView(state.activeView);
  const diff = state.beatmap?.difficulties[state.activeDifficulty];
  const range = vr ? effectiveAreaRange(state.areaRange, state.areaGesture, state.playback.currentBeat) : null;
  const bpm = state.beatmap?.bpm || 120;
  // A node's beat decodes from its encoded time (z = ms × 0.02).
  const nodeInArea = (noteIndex: number, z: number) => {
    const note = diff?.notes[noteIndex];
    return !!range && !!note && state.areaFilters[noteFilterKind(note)] && inRange((z / 0.02 / 1000) * (bpm / 60), range);
  };
  if (t.kind === 'railNode' && nodeInArea(t.noteIndex, grabbed.z)) {
    const nodes: DragMember[] = [];
    diff!.notes.forEach((note, index) => {
      note.rail_nodes?.forEach((node, nodeIndex) => {
        if (nodeInArea(index, node[2])) {
          nodes.push({ target: { kind: 'railNode', noteIndex: index, nodeIndex }, start: [node[0], node[1]], z: node[2] });
        }
      });
    });
    return nodes;
  }
  const inSelection = (t.kind === 'note' && state.selectedNoteIds.includes(t.index))
    || (t.kind === 'slide' && state.selectedSlideIds.includes(t.index))
    || (vr && t.kind === 'square' && state.selectedSquares.includes(t.index))
    || (vr && t.kind === 'triangle' && state.selectedTriangles.includes(t.index))
    || (vr && t.kind === 'crouch' && state.selectedCrouches.some(ms => Math.abs(ms - t.timeMs) <= CROUCH_MATCH_MS));
  const targets: DragTarget[] = [
    ...state.selectedNoteIds.map((index): DragTarget => ({ kind: 'note', index })),
    ...state.selectedSlideIds.map((index): DragTarget => ({ kind: 'slide', index })),
    ...(vr ? state.selectedSquares.map((index): DragTarget => ({ kind: 'square', index })) : []),
    ...(vr ? state.selectedTriangles.map((index): DragTarget => ({ kind: 'triangle', index })) : []),
    ...(vr ? state.selectedCrouches.map((timeMs): DragTarget => ({ kind: 'crouch', timeMs })) : []),
  ];
  if (!inSelection || targets.length < 2) return [grabbed];
  const group: DragMember[] = [];
  for (const target of targets) {
    const m = memberOf(state, target);
    if (m) group.push(m);
  }
  return group;
}

// ── The drag ────────────────────────────────────────────────────────────────

const _drag = {
  active: false,
  camera: null as THREE.Camera | null,
  rect: { left: 0, top: 0, width: 0, height: 0 },
  planeZ: 0,
  startHit: new THREE.Vector3(),
  grabbed: null as DragMember | null,
  group: [] as DragMember[],
  /** A Ctrl-drag of a rail head: the head moves and its nodes stay. */
  headOnly: false,
  // One undo snapshot on the first move (beginDrag/endDrag), so a press without a move adds none.
  historyStarted: false,
};

/** True while an Alt-drag runs — the free camera's orbit and the box selector stand down. */
export function isObjectDragActive(): boolean {
  return _drag.active;
}

/**
 * Synth VR: a left press with Ctrl (or Cmd) held, Alt not, on a note that heads a rail. Ctrl + click adds
 * rail nodes ([SRVR-0038]), so on a rail head the same press drags the head alone instead.
 */
export function isCtrlRailHeadPress(
  e: { button?: number; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean },
  target: DragTarget, state: StoreState,
): boolean {
  if (target.kind !== 'note' || !isSynthVrView(state.activeView)) return false;
  if (!gestureMatches('drag_rail_head_only', e)) return false;   // Ctrl + Mouse0 unless rebound
  return !!state.beatmap?.difficulties[state.activeDifficulty]?.notes[target.index]?.has_rail;
}

/**
 * Start an Alt-drag from an object's pointerdown. Returns whether it started: only a left press with
 * Alt held on an object that exists. A started drag stops the event, so neither the grid nor the box
 * selector sees it.
 */
export function startObjectDrag(e: ThreeEvent<PointerEvent>, target: DragTarget): boolean {
  const state = useEditorStore.getState();
  const vr = isSynthVrView(state.activeView);
  // Synth VR, the Add Mirror Note gesture (Ctrl + Alt + Mouse0 unless rebound) on a note: the mirror, not a drag.
  if (target.kind === 'note' && vr && !_drag.active && gestureMatches('add_mirror_note', e)) {
    e.stopPropagation();
    state.addMirrorNote(target.index);
    return false;
  }
  const headOnly = isCtrlRailHeadPress(e, target, state);
  // The drag gesture: Alt + Mouse0 unless rebound in Synth VR; Alt + left press in the other views.
  const isDrag = vr ? gestureMatches('drag_snap_object', e) : (!!e.altKey && e.button === 0);
  if ((!isDrag && !headOnly) || _drag.active) return false;
  const grabbed = memberOf(state, target);
  const el = e.nativeEvent.target as Element | null;
  if (!grabbed || !el || typeof el.getBoundingClientRect !== 'function') return false;
  e.stopPropagation();
  const r = el.getBoundingClientRect();
  _drag.active = true;
  _drag.camera = e.camera;
  _drag.rect = { left: r.left, top: r.top, width: r.width, height: r.height };
  _drag.planeZ = e.point.z;
  _drag.startHit.copy(e.point);
  _drag.grabbed = grabbed;
  // A head-only drag moves the head alone, whatever else is selected.
  _drag.group = headOnly ? [grabbed] : dragGroup(state, grabbed);
  _drag.headOnly = headOnly;
  _drag.historyStarted = false;
  document.body.style.cursor = 'move';
  state.setGizmoDragAxis('x'); // a drag is running: hides the placement preview (SRVR-0023)
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  window.addEventListener('keydown', onRotateKeyDown, true);
  window.addEventListener('keyup', onRotateKeyUp, true);
  window.addEventListener('blur', stopRotateRepeat);
  return true;
}

// ── Rotating walls mid-drag (Q / E) ──────────────────────────────────────────

/** Wait after the first step before a held key repeats, then the gap between repeats: slow enough
 *  that releasing the key lands on the angle the mapper wanted. */
const ROTATE_REPEAT_DELAY_MS = 225;
const ROTATE_REPEAT_EVERY_MS = 110;
/** z_rotation sign of a clockwise turn on screen (positive z_rotation turns anticlockwise, as in Unity). */
const CLOCKWISE_SIGN = -1;

const _rot = { key: null as string | null, delay: 0, repeat: 0 };

/** Start the undo snapshot the first time the drag changes anything, move or rotation. */
function ensureDragHistory(store: StoreState) {
  if (_drag.historyStarted) return;
  store.beginDrag(_drag.headOnly ? 'Move rail head' : dragLabel(_drag.group));
  _drag.historyStarted = true;
}

/** Turn every rotatable wall in the drag group one step. Crouches never rotate; notes are not walls. */
function rotateGroupStep(sign: 1 | -1) {
  if (!_drag.active) return;
  const store = useEditorStore.getState();
  if (!isSynthVrView(store.activeView)) return; // wall rotation is Synth VR's; other views keep their walls as they were
  const targets = _drag.group.flatMap(m =>
    m.target.kind === 'slide' || m.target.kind === 'square' || m.target.kind === 'triangle'
      ? [{ kind: m.target.kind, index: m.target.index }] : []);
  if (!targets.length) return;
  ensureDragHistory(store);
  store.rotateObstacles(targets, sign * CLOCKWISE_SIGN * WALL_ROTATION_STEP);
}

function stopRotateRepeat() {
  clearTimeout(_rot.delay);
  clearInterval(_rot.repeat);
  _rot.key = null;
}

function onRotateKeyDown(e: KeyboardEvent) {
  if (!_drag.active) return;
  if (!isSynthVrView(useEditorStore.getState().activeView)) return;   // wall rotation is Synth VR's; the other views keep Q and E
  // The keys are the ones bound to the two rotation rows (Alt + E and Alt + Q unless rebound).
  const cw = keyGestureMatches('rotate_wall_cw', e);
  if (!cw && !keyGestureMatches('rotate_wall_ccw', e)) return;
  // Those keys belong to the drag while it runs: nothing else (the eraser, the camera) sees them.
  e.preventDefault();
  e.stopImmediatePropagation();
  if (e.repeat || _rot.key === e.code) return; // the repeat is ours, not the OS's
  stopRotateRepeat();
  _rot.key = e.code;
  const sign = cw ? 1 : -1;
  rotateGroupStep(sign);
  _rot.delay = window.setTimeout(() => {
    _rot.repeat = window.setInterval(() => rotateGroupStep(sign), ROTATE_REPEAT_EVERY_MS);
  }, ROTATE_REPEAT_DELAY_MS);
}

function onRotateKeyUp(e: KeyboardEvent) {
  if (e.code === _rot.key) stopRotateRepeat();
}

function onMove(e: PointerEvent) {
  if (!_drag.active || !_drag.camera || !_drag.grabbed) return;
  const hit = pointerOnPlane(e.clientX, e.clientY, _drag.rect, _drag.camera, _drag.planeZ);
  if (!hit) return; // edge-on plane: hold still until the ray meets it again
  const store = useEditorStore.getState();
  const plan = planMove(
    _drag.grabbed, _drag.group, noteDelta(_drag.startHit, hit),
    store.snapToGrid ? store.snapToGridPosition : null,
    clampToPlacementLimits,
    isSynthVrView(store.activeView),   // the rigid group is Synth VR's; other views clamp the grabbed note alone
    store.snapToGrid ? store.snapWallToGridPosition : null,
  );
  // Snap to Rail (Synth VR): a plain note dragged ALONE over a concurrent rail of its type lands on the
  // rail's line. A rail head carries its rail and is left alone. A group is not snapped, whatever it holds
  // (a note and a wall are a group of two): it moves as one rigid body (SRVR-0018 AC11), and snapping a
  // member against the rail where the store still holds it would leave it a step behind a rail that moves
  // in the same frame, or pull the note away from a wall that moved by the plain offset.
  const noteList = store.getCurrentDifficulty()?.notes;
  if (noteList && plan.notes.length === 1 && _drag.group.length === 1) {
    for (const n of plan.notes) {
      const src = noteList[n.index];
      if (!src || src.has_rail) continue;
      const [sx, sy] = store.snapToRailPosition(n.position[0], n.position[1], src.note_type, src.time_beats, n.index);
      if (sx !== n.position[0] || sy !== n.position[1]) { n.position = [sx, sy, n.position[2]]; n.offGrid = true; }   // the rail may leave the grid
    }
  }
  ensureDragHistory(store);
  // moveSelectedNotes, not updateNote: a rail head carries its whole rail with it. A head-only drag is
  // the exception, and updateNote moves the note alone.
  if (_drag.headOnly) for (const n of plan.notes) store.updateNote(n.index, { position: n.position }, n.offGrid);
  else if (plan.notes.length) store.moveSelectedNotes(plan.notes);
  for (const r of plan.railNodes) store.updateRailNode(r.noteIndex, r.nodeIndex, r.position, r.offGrid);
  if (plan.obstacles.length) store.moveObstacles(plan.obstacles);
}

function onUp() {
  if (!_drag.active) return;
  const store = useEditorStore.getState();
  stopRotateRepeat();
  window.removeEventListener('keydown', onRotateKeyDown, true);
  window.removeEventListener('keyup', onRotateKeyUp, true);
  window.removeEventListener('blur', stopRotateRepeat);
  if (_drag.historyStarted) store.endDrag();
  _drag.active = false;
  _drag.camera = null;
  _drag.grabbed = null;
  _drag.group = [];
  _drag.headOnly = false;
  _drag.historyStarted = false;
  document.body.style.cursor = '';
  store.setGizmoDragAxis(null);
  window.removeEventListener('pointermove', onMove);
  window.removeEventListener('pointerup', onUp);
  window.removeEventListener('pointercancel', onUp);
}

/** End any drag in progress, as a pointerup would. For tests and teardown. */
export function cancelObjectDrag(): void {
  onUp();
}
